(function () {
  function init() {
    if (typeof L === 'undefined' || typeof L.markerClusterGroup !== 'function') {
      // Leaflet (or markercluster) not loaded yet — retry once
      setTimeout(init, 100);
      return;
    }

    const root = document.querySelector('[data-locations-root]');
    if (!root) return;

    const dataEl = root.querySelector('[data-locations-data]');
    const mapEl = root.querySelector('[data-locations-map]');
    const listEl = root.querySelector('[data-locations-list]');
    const searchEl = root.querySelector('[data-locations-search]');
    const regionsEl = root.querySelector('[data-locations-regions]');
    const nearMeBtn = root.querySelector('[data-locations-near-me]');
    const viewTabs = root.querySelector('[data-locations-view-tabs]');
    const layoutEl = root.querySelector('.locations__layout');
    if (!dataEl || !mapEl) return;

    const stockists = JSON.parse(dataEl.textContent);

    // ---------- Map setup ----------
    const map = L.map(mapEl, {
      center: [25, 10],
      zoom: 2,
      worldCopyJump: true,
      scrollWheelZoom: true,
    });

    // CARTO Positron — clean light-gray basemap, on-brand for editorial sites.
    // CARTO now requires a (free) key; without it every tile carries an
    // "API key required" watermark. The key comes from the section setting.
    const tilesKey = mapEl.dataset.tilesKey;
    const tilesUrl =
      'https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png' +
      (tilesKey ? `?key=${encodeURIComponent(tilesKey)}` : '');
    L.tileLayer(tilesUrl, {
      maxZoom: 19,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
    }).addTo(map);

    // Marker cluster group: collapses overlapping pins (e.g. NYC has 5+).
    // disableClusteringAtZoom + larger spiderfyDistanceMultiplier so clicking
    // a cluster reveals distinct markers instead of overlapping dots.
    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      spiderfyDistanceMultiplier: 1.8,
      disableClusteringAtZoom: 11,
      maxClusterRadius: 40,
      iconCreateFunction: (cluster) => {
        return L.divIcon({
          html: `<div class="locations__cluster-inner">${cluster.getChildCount()}</div>`,
          className: 'locations__cluster',
          iconSize: [32, 32],
        });
      },
    });
    map.addLayer(clusterGroup);

    // City-level geocoding gives multiple stockists in the same city
    // identical coords. Apply a deterministic small jitter (~50–150m radius)
    // so each marker has a unique position, which makes spiderfy and
    // hover/click usable when there are many stockists in one city.
    function jitterCoords(stockist) {
      if (stockist.lat == null || stockist.lng == null) return [null, null];
      const idStr = String(stockist.id || stockist.name || '');
      let hash = 0;
      for (let i = 0; i < idStr.length; i++) {
        hash = (hash * 31 + idStr.charCodeAt(i)) | 0;
      }
      // Map hash to angle 0..2π and radius ~0.0008 deg (~80m at equator)
      const angle = ((hash % 360) + 360) % 360 * (Math.PI / 180);
      const radius = 0.0008 + ((hash >> 8) & 0x3f) * 0.00002;
      return [stockist.lat + Math.cos(angle) * radius, stockist.lng + Math.sin(angle) * radius];
    }

    // ---------- Markers ----------
    const markersById = new Map();

    function buildIcon(active) {
      return L.divIcon({
        className: 'locations__marker' + (active ? ' locations__marker--active' : ''),
        iconSize: [12, 12],
      });
    }

    const initialBounds = [];
    stockists.forEach((s) => {
      const [lat, lng] = jitterCoords(s);
      if (lat == null || lng == null) return;
      const marker = L.marker([lat, lng], { icon: buildIcon(false) });
      const url = s.url
        ? `<a class="locations__popup-link" href="${s.url}" target="_blank" rel="noopener">Visit website</a>`
        : '';
      marker.bindPopup(
        `<div class="locations__popup">
          <h4 class="locations__popup-name">${escapeHtml(s.name)}</h4>
          <p class="locations__popup-city">${escapeHtml(s.city)}, ${escapeHtml(s.country)}</p>
          ${url}
        </div>`,
        { closeButton: false }
      );
      marker.on('click', () => focusEntry(s.id));
      markersById.set(s.id, marker);
      clusterGroup.addLayer(marker);
      initialBounds.push([lat, lng]);
    });

    if (initialBounds.length) {
      map.fitBounds(initialBounds, { padding: [40, 40], maxZoom: 4 });
    }

    // ---------- Sidebar list (built from JSON, not Liquid, to bypass 50-cap) ----------
    const entryButtonsById = new Map();
    const countryCounts = new Map();
    stockists.forEach((s) => {
      countryCounts.set(s.country, (countryCounts.get(s.country) || 0) + 1);
    });

    const sortedForList = stockists.slice().sort((a, b) => {
      const co = (a.country || '').localeCompare(b.country || '');
      if (co !== 0) return co;
      return (a.name || '').localeCompare(b.name || '');
    });

    let lastCountry = null;
    let currentCountryUl = null;
    sortedForList.forEach((s) => {
      if (s.country !== lastCountry) {
        const countryLi = document.createElement('li');
        countryLi.className = 'locations__country';
        countryLi.dataset.country = s.country;
        const heading = document.createElement('h3');
        heading.className = 'locations__country-heading';
        heading.innerHTML = `<span>${escapeHtml(s.country) || '—'}</span><span class="locations__country-count">${countryCounts.get(s.country)}</span>`;
        countryLi.appendChild(heading);
        currentCountryUl = document.createElement('ul');
        currentCountryUl.className = 'list-unstyled locations__country-list';
        currentCountryUl.setAttribute('role', 'list');
        countryLi.appendChild(currentCountryUl);
        listEl.appendChild(countryLi);
        lastCountry = s.country;
      }
      const li = document.createElement('li');
      li.className = 'locations__item';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'locations__entry';
      btn.dataset.stockistId = s.id;
      btn.dataset.region = s.region || '';
      btn.dataset.country = s.country || '';
      btn.dataset.searchKey = `${(s.name || '').toLowerCase()} ${(s.city || '').toLowerCase()} ${(s.country || '').toLowerCase()}`;
      btn.innerHTML =
        `<span class="locations__entry-name">${escapeHtml(s.name)}</span>` +
        `<span class="locations__entry-city">${escapeHtml(s.city)}</span>`;
      btn.addEventListener('click', () => {
        flyToStockist(s.id);
        focusEntry(s.id);
        // On mobile, switch to map tab when user picks a stockist
        if (window.matchMedia('(max-width: 989px)').matches) setView('map');
      });
      li.appendChild(btn);
      currentCountryUl.appendChild(li);
      entryButtonsById.set(s.id, btn);
    });

    // ---------- Active entry / pin focus ----------
    let activeId = null;
    function focusEntry(id) {
      if (activeId && activeId !== id) {
        const prev = entryButtonsById.get(activeId);
        if (prev) prev.classList.remove('locations__entry--active');
        const prevMarker = markersById.get(activeId);
        if (prevMarker) prevMarker.setIcon(buildIcon(false));
      }
      activeId = id;
      const btn = entryButtonsById.get(id);
      if (btn) {
        btn.classList.add('locations__entry--active');
        btn.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      const marker = markersById.get(id);
      if (marker) marker.setIcon(buildIcon(true));
      updateHash({ stockist: id });
    }

    function flyToStockist(id) {
      const marker = markersById.get(id);
      if (!marker) return;
      // markerCluster's zoomToShowLayer ensures the marker becomes visible
      // (uncluster) before we open its popup.
      clusterGroup.zoomToShowLayer(marker, () => {
        map.flyTo(marker.getLatLng(), Math.max(map.getZoom(), 10), { duration: 0.6 });
        marker.openPopup();
      });
    }

    // ---------- Region filter chips ----------
    const regionCounts = new Map();
    stockists.forEach((s) => {
      if (s.region) regionCounts.set(s.region, (regionCounts.get(s.region) || 0) + 1);
    });
    const regions = ['All', ...Array.from(regionCounts.keys()).sort()];
    let activeRegion = 'All';

    regions.forEach((r) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'locations__region' + (r === 'All' ? ' locations__region--active' : '');
      btn.dataset.region = r;
      btn.textContent = r === 'All' ? `All (${stockists.length})` : `${r} (${regionCounts.get(r)})`;
      btn.addEventListener('click', () => setRegion(r));
      regionsEl.appendChild(btn);
    });

    function setRegion(r) {
      activeRegion = r;
      regionsEl.querySelectorAll('.locations__region').forEach((b) => {
        b.classList.toggle('locations__region--active', b.dataset.region === r);
      });
      applyFilters();
      updateHash({ region: r === 'All' ? null : r.toLowerCase().replace(/\s+/g, '-') });
    }

    // ---------- Search ----------
    let searchTerm = '';
    searchEl.addEventListener('input', (e) => {
      searchTerm = e.target.value.trim().toLowerCase();
      applyFilters();
    });

    function applyFilters() {
      const visibleMarkerCoords = [];
      let visibleCount = 0;

      listEl.querySelectorAll('[data-stockist-id]').forEach((btn) => {
        const item = btn.closest('.locations__item');
        const region = btn.dataset.region;
        const key = btn.dataset.searchKey || '';
        const regionMatch = activeRegion === 'All' || region === activeRegion;
        const searchMatch = !searchTerm || key.includes(searchTerm);
        const visible = regionMatch && searchMatch;
        if (item) item.classList.toggle('locations__item--hidden', !visible);

        const id = btn.dataset.stockistId;
        const marker = markersById.get(id);
        if (marker) {
          if (visible) {
            if (!clusterGroup.hasLayer(marker)) clusterGroup.addLayer(marker);
            visibleMarkerCoords.push(marker.getLatLng());
            visibleCount++;
          } else if (clusterGroup.hasLayer(marker)) {
            clusterGroup.removeLayer(marker);
          }
        }
      });

      // Hide country headings whose children are all hidden
      listEl.querySelectorAll('.locations__country').forEach((c) => {
        const visibleChildren = c.querySelectorAll('.locations__item:not(.locations__item--hidden)');
        c.style.display = visibleChildren.length ? '' : 'none';
      });

      if (visibleMarkerCoords.length) {
        map.fitBounds(visibleMarkerCoords, { padding: [40, 40], maxZoom: 5 });
      }

      // Empty state
      let empty = listEl.querySelector('.locations__empty-filter');
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'locations__empty-filter';
        empty.textContent = 'No stockists match your filters.';
        listEl.appendChild(empty);
      }
      empty.style.display = visibleCount === 0 ? 'block' : 'none';
    }

    // ---------- Geolocation: Near-me button + initial auto-detect ----------
    function handleGeolocate(showError) {
      if (!('geolocation' in navigator)) {
        if (showError) alert('Geolocation is not available in this browser.');
        return;
      }
      if (nearMeBtn) nearMeBtn.classList.add('locations__near-me--loading');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (nearMeBtn) nearMeBtn.classList.remove('locations__near-me--loading');
          const userLat = pos.coords.latitude;
          const userLng = pos.coords.longitude;
          const nearest = findNearestStockist(userLat, userLng);
          if (!nearest) return;

          // Mark user position with a dot
          if (window._userLocationMarker) map.removeLayer(window._userLocationMarker);
          window._userLocationMarker = L.circleMarker([userLat, userLng], {
            radius: 6,
            color: '#0066ff',
            fillColor: '#0066ff',
            fillOpacity: 0.4,
            weight: 2,
          })
            .addTo(map)
            .bindPopup('You are here', { closeButton: false });

          flyToStockist(nearest.id);
          focusEntry(nearest.id);
        },
        () => {
          if (nearMeBtn) nearMeBtn.classList.remove('locations__near-me--loading');
          if (showError) alert('Could not determine your location. Please allow location access and try again.');
        },
        { timeout: 8000, maximumAge: 600000 }
      );
    }

    if (nearMeBtn) {
      nearMeBtn.addEventListener('click', () => handleGeolocate(true));
    }

    // Soft auto-detect on first load (don't surface errors)
    handleGeolocate(false);

    function findNearestStockist(lat, lng) {
      let best = null;
      let bestDist = Infinity;
      stockists.forEach((s) => {
        if (s.lat == null || s.lng == null) return;
        const d = haversine(lat, lng, s.lat, s.lng);
        if (d < bestDist) {
          bestDist = d;
          best = s;
        }
      });
      return best;
    }

    function haversine(lat1, lon1, lat2, lon2) {
      const toRad = (deg) => (deg * Math.PI) / 180;
      const R = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(a));
    }

    // ---------- Mobile view tabs (List / Map) ----------
    function setView(view) {
      if (!layoutEl) return;
      layoutEl.dataset.view = view;
      if (viewTabs) {
        viewTabs.querySelectorAll('.locations__view-tab').forEach((t) => {
          const isActive = t.dataset.view === view;
          t.classList.toggle('locations__view-tab--active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
      }
      // Map needs to recompute size when revealed
      if (view === 'map') setTimeout(() => map.invalidateSize(), 50);
    }

    if (viewTabs) {
      viewTabs.querySelectorAll('.locations__view-tab').forEach((t) => {
        t.addEventListener('click', () => setView(t.dataset.view));
      });
    }

    // ---------- URL hash state ----------
    // Format: #region=europe&stockist=<id> — reads on load, writes on change.
    function readHash() {
      const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const region = params.get('region');
      const stockist = params.get('stockist');
      if (region) {
        const match = regions.find((r) => r.toLowerCase().replace(/\s+/g, '-') === region);
        if (match) setRegion(match);
      }
      if (stockist) {
        // Wait for cluster group to be ready, then fly
        setTimeout(() => {
          if (markersById.has(stockist)) {
            flyToStockist(stockist);
            focusEntry(stockist);
          }
        }, 100);
      }
    }

    function updateHash(patch) {
      const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null) params.delete(k);
        else params.set(k, v);
      });
      const str = params.toString();
      const newHash = str ? '#' + str : '';
      if (newHash !== window.location.hash) {
        history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
      }
    }

    readHash();
  }

  function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
